<template>
  <ModalFade
    class="gallery"
    @close="closeGallery"
  >
    <button
      class="gallery__close-button"
      @click="closeGallery"
    >
      <SvgAsset
        class="gallery__close-icon"
        :content="galleryCrossPath"
      />
    </button>
    <ScrollButton
      v-show="isBigTabletAndMore"
      ref="scrollLeft"
      class="gallery__button gallery__button--left"
    />
    <div
      ref="gallery"
      class="gallery__wrapper"
      @mousedown.self="closeGallery"
    >
      <div
        class="swiper-wrapper gallery__track"
        @mousedown.self="$emit('close')"
      >
        <div
          v-for="(slide, index) of currentSlides"
          :key="`${slide}--${index}`"
          class="swiper-slide gallery__slide"
          @click.self="$emit('close')"
        >
          <img
            class="gallery__slide-img"
            :src="slide"
          />
        </div>
        <span
          v-if="!currentSlides.length"
          class="gallery__fallback-text"
        >
          В режиме предпросмотра статьи изображение недоступно.
        </span>
      </div>
    </div>
    <ScrollButton
      v-show="isBigTabletAndMore"
      ref="scrollRight"
      class="gallery__button gallery__button--right"
    />
  </ModalFade>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { Swiper } from 'swiper';
  import ScrollButton from '~/components/BaseComponents/ScrollButton.vue';
  import ModalFade from '~/components/ModalFade.vue';
  import { useWindowStore } from '~/store/window';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import galleryCrossPath from '@/assets/images/svg/gallery-cross.svg?raw';

  export default defineNuxtComponent({
    name: 'FullscreenGallery',
    components: {
      SvgAsset,
      ScrollButton,
      ModalFade,
    },

    props: {
      slides: {
        type: Array as PropType<string[]>,
        default: null,
      },

      closeClickOutside: {
        type: Boolean as PropType<boolean>,
        default: true,
      },

      activeSlide: {
        type: String as PropType<string>,
        default: null,
      },
    },

    data: () => ({
      swiper: {} as Swiper,
    }),

    computed: {
      galleryCrossPath: () => galleryCrossPath,
      isBigTabletAndMore() {
        return useWindowStore().isBigTabletAndMore;
      },

      fullscreenSlides() {
        return useSingleArticleStore().fullscreenSlides;
      },

      currentSlides(): string[] {
        if (!this.slides) {
          return this.fullscreenSlides;
        }

        return this.slides;
      },
    },

    async mounted() {
      const { default: Swiper } = await import('swiper');
      const { Navigation, Keyboard } = await import('swiper/modules');

      const articlesCarouselWrapp = this.$refs.gallery as HTMLElement;
      this.swiper = new Swiper(articlesCarouselWrapp, {
        modules: [Navigation, Keyboard],
        loop: true,
        slidesPerView: 1,
        spaceBetween: 12,
        navigation: {
          nextEl: '.gallery__button--right',
          prevEl: '.gallery__button--left',
        },

        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
      });

      if (this.activeSlide) {
        const slideIndex = this.currentSlides.indexOf(this.activeSlide);
        this.swiper.slideTo(slideIndex);
      }
    },

    methods: {
      closeGallery() {
        this.$emit('close');
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use 'swiper/scss' as *;

  .gallery {
    display: flex;
    align-items: center;

    &__close-button {
      z-index: 2;
      position: absolute;
      width: 32px;
      height: 32px;

      top: 65px;
      right: 16px;

      @include tablet {
        top: 65px;
        right: 24px;
      }

      @include hover {
        .gallery__close-icon {
          :deep() {
            svg path {
              fill: #fff;
            }
          }
        }
      }
    }

    &__fallback-text {
      color: white;
      margin: auto;
    }

    &__close-icon {
      :deep() {
        svg path {
          fill: #acb;
        }
      }
    }

    &__button {
      flex-shrink: 0;
      &--left {
        transform: scale(-1);
        margin-left: $spacing-6;
        margin-right: $spacing-4;
      }
      &--right {
        margin-left: $spacing-4;
        margin-right: $spacing-6;
      }
    }

    &__wrapper {
      height: 100%;
      width: 100%;
      max-height: 80%;
      margin: 0 auto;
      overflow: hidden;

      @include big-tablet {
        max-height: 608px;
        max-width: 1120px;
      }
    }

    &__slide {
      display: flex;
      justify-content: center;
    }

    &__slide-img {
      height: 100%;
      max-width: 100%;
      object-fit: contain;
      display: block;
    }
  }
</style>
